//Financial mapping
import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  useTheme,
  useMediaQuery,
  Chip,
  Divider,
  Button,
  FormControl,
  MenuItem,
  Select,
} from "@mui/material";
import UserLayout from "components/layouts/UserLayout";
import { useNavigate } from "react-router-dom";
import Icon from "components/icon";

const AccountMapping = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [mappings, setMappings] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);

  const outdoorlessAccounts = [
    { id: "acc1", name: "Operating Account" },
    { id: "acc2", name: "Revenue Account" },
    { id: "acc3", name: "Expense Account" },
    { id: "acc4", name: "Tax Account" },
    { id: "acc5", name: "Refund Account" },
  ];

  const clientAccounts = [
    {
      id: "inv_account1",
      name: "Cash - Operating",
      type: "Cleri Account",
      required: true,
    },
    {
      id: "inv_payments1",
      name: "Account Receivable",
      type: "Cleri Account",
      required: true,
    },
    {
      id: "pay_refunds1",
      name: "Equipment",
      type: "Cleri Account",
      required: false,
    },
    {
      id: "cust_pay_deposits",
      name: "Accounts Payable",
      type: "Cleri Account",
      required: true,
    },
    {
      id: "pay_refunds4",
      name: "Revenue - Services",
      type: "Cleri Account",
      required: true,
    },
    {
      id: "inv_account2",
      name: "Revenue - Materials",
      type: "Cleri Account",
      required: true,
    },
    {
      id: "inv_account3",
      name: "Cost of Goods Sold",
      type: "Cleri Account",
      required: true,
    },
    {
      id: "pay_refunds3",
      name: "Labor Costs",
      type: "Cleri Account",
      required: true,
    },
    {
      id: "inv_payments2",
      name: "Equipment Rental",
      type: "Cleri Account",
      required: false,
    },
    {
      id: "pay_refunds5",
      name: "Office Supplies",
      type: "Cleri Account",
      required: false,
    },
  ];

  const handleAccountChange = (clientAccountId, outdoorlessAccountId) => {
    setMappings((prev) => ({
      ...prev,
      [clientAccountId]: outdoorlessAccountId,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1000));

    console.log("Submitted mappings:", mappings);
    setIsProcessing(false);
  };

  const getUnmappedRequiredCount = () => {
    return clientAccounts.filter(
      (account) => account.required && !mappings[account.id]
    ).length;
  };

  const unmappedRequiredCount = getUnmappedRequiredCount();
  const canProceed = unmappedRequiredCount === 0;
  const handleNavigation = (to) => {
    navigate(to);
  };
  const handlePageBack = () => {
    handleNavigation("/quickbooks");
  };

  return (
    <UserLayout
      contentHeightFixed={false}
      NavURL={handlePageBack}
      showIcon={true}
      activeLink="Quickbooks/Financial Mapping"
    >
      <Box sx={{ p: { xs: 1, md: 1 }, mx: "auto" }}>
        <Card sx={{ borderRadius: 1, overflow: "hidden" }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                <Chip
                  icon={
                    <Icon
                      icon="radix-icons:dot-filled"
                      width={20}
                      height={20}
                      style={{ color: "#DD992F" }}
                    />
                  }
                  label={`${unmappedRequiredCount} Required missing`}
                  size="medium"
                  sx={{
                    mt: 2,
                    mr: 2,
                    height: 26,
                    fontSize: "0.75rem",
                    backgroundColor: "#FDF8EC",
                    color: "#DD992F",
                    fontWeight: 600,
                    "& .MuiChip-label": {
                      px: 2.5,
                    },
                  }}
                />
                <Button
                  variant="contained"
                  color="main"
                  size="medium"
                  startIcon={
                    <Icon
                      icon={
                        isProcessing
                          ? "eos-icons:loading"
                          : "material-symbols:save"
                      }
                      width={20}
                      height={20}
                    />
                  }
                  onClick={handleSubmit}
                >
                  Save Mappings
                </Button>
              </Box>

              {/* Full Width Alert Banner */}
              <Box
                sx={{
                  backgroundColor: !canProceed ? "#FFFBF0" : "#F0FFF4",
                  border: "1px solid",
                  borderColor: !canProceed ? "#DD992F" : "#68D391",
                  color: !canProceed ? "#8A6500" : "#2D5A3C",
                  padding: "16px",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "12px",
                  fontSize: "17px",
                  lineHeight: 1.5,
                  width: "100%",
                }}
              >
                <Icon
                  icon={
                    !canProceed
                      ? "solar:danger-triangle-outline"
                      : "hugeicons:checkmark-circle-03"
                  }
                  width={20}
                  height={20}
                />
             {!canProceed ? (
              `You must map all required accounts before proceeding. 
              ${unmappedRequiredCount} required accounts are still unmapped. 
              This mapping is a dependency for invoices and payments modules.`
            ) : (
              "All required accounts are mapped. You can now proceed with other modules."
            )}
              </Box> 
            </Box>

            {/* Account Mapping Section */}
            <Card
              sx={{
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.paper",
                mb: 4,
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                  Account Mapping
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Map each Client account to the corresponding QuickBooks
                  account. Required accounts must be mapped before other modules
                  can sync.
                </Typography>

                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    {clientAccounts.map((account) => (
                      <Grid item xs={12} key={account.id}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 3,
                            p: 3,
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: "divider",
                            backgroundColor: "background.paper",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              backgroundColor: "action.hover",
                            },
                          }}
                        >
                         
                          {mappings[account.id] ? (
                            <Chip
                              icon={
                                <Icon
                                  icon="hugeicons:checkmark-circle-03"
                                  width={20}
                                  height={20}
                                  style={{ color: "#23A26D" }}
                                />
                              }
                              size="small"
                              sx={{
                                height: 28,
                                fontSize: "0.75rem",
                                backgroundColor: "#E8F5E9",
                                color: "#23A26D",
                                fontWeight: 600,
                                "& .MuiChip-label": {
                                  px: 1,
                                  pl: 1,
                                },
                              }}
                            />
                          ) : (
                            <Icon
                              icon="mi:circle"
                              width={20}
                              height={20}
                              style={{ color: "#666666" }}
                            />
                          )}

                          <Box
                            sx={{
                              flex: 1,
                              display: "flex",
                              alignItems: "center",
                              gap: 5,
                            }}
                          >
                            <Box>
                              <Typography variant="h6">
                                {account.name}
                              </Typography>
                              <Typography
                                variant="body1"
                                color="text.secondary"
                              >
                                {account.type}
                              </Typography>
                            </Box>

                            {/* Badge Chip */}
                            <Chip
                              label="Badge"
                              size="small"
                              sx={{
                                height: 24,
                                fontSize: "0.75rem",
                                backgroundColor: "#f0f0f1",
                                color: "#666666",
                                fontWeight: 500,
                                "& .MuiChip-label": {
                                  px: 2,
                                },
                              }}
                            />

                            {/* Required Chip */}
                            {account.required && (
                              <Chip
                                label="Required"
                                size="small"
                                sx={{
                                  height: 24,
                                  fontSize: "0.75rem",
                                  backgroundColor: "#FFEBEE",
                                  color: "#D80027",
                                  fontWeight: 600,
                                  "& .MuiChip-label": {
                                    px: 2,
                                  },
                                }}
                              />
                            )}
                          </Box>

                          {/* Arrow Icon before Select Dropdown */}
                          <Icon
                            icon="zondicons:arrow-right"
                            width={20}
                            height={20}
                            style={{ color: "#666666" }}
                          />

                          {/* Select Dropdown */}
                          <FormControl sx={{ minWidth: 450 }}>
                            <Select
                              value={mappings[account.id] || ""}
                              onChange={(e) =>
                                handleAccountChange(account.id, e.target.value)
                              }
                              displayEmpty
                              required={account.required}
                              sx={{
                                height: 40,
                                "& .MuiSelect-select": {
                                  py: 1,
                                },
                              }}
                            >
                              <MenuItem value="">
                                <Typography color="text.secondary">
                                  Select...
                                </Typography>
                              </MenuItem>
                              {outdoorlessAccounts.map((oa) => (
                                <MenuItem key={oa.id} value={oa.id}>
                                  {oa.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  {/* Action Buttons at Bottom */}
                </form>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </Box>
    </UserLayout>
  );
};

export default AccountMapping;
